<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component
  // ============================================================
  // Follow/Unfollow toggle button with realtime state

  import { apiClient } from '../../utils/api';

  interface Props {
    userId: number;
    initialFollowing?: boolean;
    size?: 'sm' | 'md';
    onToggle?: (isFollowing: boolean) => void;
  }

  let { userId, initialFollowing = false, size = 'md', onToggle }: Props = $props();
  // svelte-ignore state_referenced_locally
  const initialFollowingValue = initialFollowing;

  let isFollowing = $state(initialFollowingValue);
  let isLoading = $state(false);
  let hasChecked = $state(initialFollowingValue !== undefined);

  // Check follow status on mount if not provided
  $effect(() => {
    if (!hasChecked) {
      checkFollowStatus();
    }
  });

  async function checkFollowStatus() {
    const res = await apiClient.get<{ isFollowing: boolean }>('/api/v1/follows/check', {
      params: { userId }
    });
    if (res && typeof res === 'object' && 'isFollowing' in res) {
      isFollowing = (res as { isFollowing: boolean }).isFollowing;
    }
    hasChecked = true;
  }

  async function toggleFollow() {
    if (isLoading) return;
    isLoading = true;

    try {
      // apiClient 는 4xx/5xx 에 throw 하지 않고 { success: false } 를 반환한다
      // (utils/api.ts). catch 만으로는 실패를 못 잡으므로 success 를 확인한다.
      if (isFollowing) {
        const r = await apiClient.delete(`/api/v1/follows/${userId}`);
        if (!r.success) return;
        isFollowing = false;
      } else {
        const r = await apiClient.post('/api/v1/follows', { userId });
        if (!r.success) return;
        isFollowing = true;
      }
      onToggle?.(isFollowing);
    } catch (err) {
      console.error('Follow toggle failed:', err);
    } finally {
      isLoading = false;
    }
  }

  const btnClass = $derived(() => {
    const base = size === 'sm'
      ? 'px-3 py-1.5 text-xs rounded-lg'
      : 'px-4 py-2 text-sm rounded-xl';

    if (isFollowing) {
      return `${base} bg-surface-tertiary text-gray-700 dark:text-dark-text-medium border border-gray-300 dark:border-dark-border hover:bg-red-50 hover:text-red-600 hover:border-red-200 dark:hover:bg-red-900/20 dark:hover:text-red-400 group`;
    }
    return `${base} bg-primary text-white hover:bg-primary-dark shadow-sm`;
  });
</script>

<button
  onclick={toggleFollow}
  disabled={isLoading || !hasChecked}
  class="font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed {btnClass()}"
>
  {#if isLoading}
    <svg class="animate-spin w-4 h-4 inline-block" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
    </svg>
  {:else if isFollowing}
    <span class="group-hover:hidden">팔로잉</span>
    <span class="hidden group-hover:inline text-red-600 dark:text-red-400">언팔로우</span>
  {:else}
    팔로우
  {/if}
</button>
